import React, { useState, useEffect } from 'react';
import { Icon, Button, Input } from '@/components/atoms';
import { Modal } from '@/components/molecules';
import { useWarehousesList, useCalculateDiscountMutation } from '@/hooks/useErpApi';
import { useUserSearch } from '@/hooks/useAuth';
import type { CartItem } from './SalesCart';
import type { DiscountValidation } from '@/schemas/discounts';

// Customer info from search
interface CustomerInfo {
  id: string;
  name?: string;
  phone: string;
  isVerified: boolean; // true if found in system, false if using mobile as ID
}

interface SalesCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  defaultWarehouseId?: string;
  onConfirmSale: (data: SaleCheckoutData) => Promise<void>;
}

export interface SaleCheckoutData {
  warehouse_id: string;
  sale_date?: string;
  sale_type: 'in_store' | 'delivery';
  payment_mode: 'cash' | 'upi' | 'online';
  customer_phone?: string;
  customer_name?: string;
  is_org_member?: boolean;
  items: Array<{
    variant_id: string;
    quantity: number;
  }>;
  apply_taxes: boolean;
  delivery_state?: string;
  discount_id?: string | null;
  coupon_code?: string | null;
  auto_apply_discounts?: boolean;
}

export const SalesCheckout: React.FC<SalesCheckoutProps> = ({
  isOpen,
  onClose,
  cartItems,
  defaultWarehouseId,
  onConfirmSale,
}) => {
  const { data: warehouses } = useWarehousesList();
  const calculateDiscountMutation = useCalculateDiscountMutation();
  const { searchByMobile, isSearching } = useUserSearch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountValidation | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  // Customer search state
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [customerSearchError, setCustomerSearchError] = useState<string | null>(null);

  const [formData, setFormData] = useState<SaleCheckoutData>({
    warehouse_id: defaultWarehouseId || '',
    sale_date: new Date().toISOString(),
    sale_type: 'in_store',
    payment_mode: 'cash',
    customer_phone: undefined,
    customer_name: undefined,
    is_org_member: false,
    items: [],
    apply_taxes: true, // Always apply taxes by default
    delivery_state: undefined,
    discount_id: null,
    coupon_code: null,
    auto_apply_discounts: true,
  });

  // Temporary state for payment mode selection (before update)
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<'cash' | 'upi' | 'online'>(
    formData.payment_mode
  );

  // Sync selectedPaymentMode with formData.payment_mode when formData changes
  useEffect(() => {
    setSelectedPaymentMode(formData.payment_mode);
  }, [formData.payment_mode]);

  // Search for customer by mobile number
  const handleCustomerSearch = async () => {
    if (!customerMobile || customerMobile.length < 10) {
      setCustomerSearchError('Please enter a valid 10-digit mobile number');
      return;
    }

    setCustomerSearchError(null);
    const user = await searchByMobile(customerMobile);

    if (user) {
      // User found in system
      setCustomerInfo({
        id: user.id,
        name: user.name,
        phone: user.phone_number || customerMobile,
        isVerified: true,
      });
      setFormData((prev) => ({
        ...prev,
        customer_phone: user.phone_number || customerMobile,
        customer_name: user.name || prev.customer_name,
        is_org_member: prev.is_org_member || false, // Preserve existing value or default to false
      }));
    } else {
      // User not found, use mobile number
      setCustomerInfo({
        id: customerMobile,
        phone: customerMobile,
        isVerified: false,
      });
      setFormData((prev) => ({
        ...prev,
        customer_phone: customerMobile,
        customer_name: prev.customer_name, // Preserve existing name if set
        is_org_member: prev.is_org_member || false,
      }));
    }
  };

  // Clear customer selection
  const handleClearCustomer = () => {
    setCustomerInfo(null);
    setCustomerMobile('');
    setFormData({
      ...formData,
      customer_phone: undefined,
      customer_name: undefined,
      is_org_member: false,
    });
    setCustomerSearchError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.warehouse_id) {
      setError('Please select a warehouse');
      return;
    }

    if (cartItems.length === 0) {
      setError('Cart is empty');
      return;
    }

    // Check for delivery type requiring customer details
    if (formData.sale_type === 'delivery' && !formData.customer_phone) {
      setError('Customer phone number is required for delivery orders');
      return;
    }

    // Validation for delivery state
    if (formData.sale_type === 'delivery' && !formData.delivery_state) {
      setError('Delivery state is required for delivery orders');
      return;
    }

    setIsSubmitting(true);

    try {
      const saleData: SaleCheckoutData = {
        ...formData,
        // Ensure customer_phone is included (undefined if not set, will be converted to null by API client)
        customer_phone: formData.customer_phone || undefined,
        customer_name: formData.customer_name || undefined,
        items: cartItems.map((item) => ({
          variant_id: item.variantId,
          quantity: item.quantity,
        })),
        coupon_code: appliedDiscount?.discount_code || null,
        discount_id: appliedDiscount?.discount_id || null,
      };

      await onConfirmSale(saleData);
      setAppliedDiscount(null);
      setDiscountCode('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create sale');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get effective price based on member status
  // When is_org_member is true, use member prices if available
  const getEffectivePrice = (item: CartItem) => {
    if (formData.is_org_member && item.memberPrice !== undefined) {
      return item.memberPrice;
    }
    return item.retailPrice ?? item.price ?? 0;
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    setIsApplyingDiscount(true);
    setDiscountError(null);

    try {
      const response = await calculateDiscountMutation.mutate({
        discount_code: discountCode.trim().toUpperCase(),
        items: cartItems.map((item) => ({
          variant_id: item.variantId,
          quantity: item.quantity,
          // Use effective price (member or retail) for discount calculation
          price: getEffectivePrice(item),
        })),
        warehouse_id: formData.warehouse_id,
      });

      if (response && response.is_valid) {
        setAppliedDiscount(response);
        setDiscountError(null);
      } else {
        setDiscountError(response?.message || 'Invalid discount code');
        setAppliedDiscount(null);
      }
    } catch (error) {
      setDiscountError(error instanceof Error ? error.message : 'Failed to apply discount');
      setAppliedDiscount(null);
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError(null);
  };

  // Calculate subtotal using effective prices (member or retail based on is_org_member)
  const subtotal = cartItems.reduce(
    (sum, item) => sum + getEffectivePrice(item) * item.quantity,
    0
  );

  // Calculate retail subtotal for comparison (to show savings)
  const retailSubtotal = cartItems.reduce(
    (sum, item) => sum + (item.retailPrice ?? item.price ?? 0) * item.quantity,
    0
  );
  const memberSavings = formData.is_org_member ? retailSubtotal - subtotal : 0;

  const discountAmount = appliedDiscount?.calculated_discount || 0;
  
  // Calculate CGST and SGST based on items' GST rates (only if apply_taxes is true)
  const cgstAmount = formData.apply_taxes
    ? cartItems.reduce((sum, item) => {
        const itemTotal = getEffectivePrice(item) * item.quantity;
        const cgstRate = item.cgst_rate ?? 0;
        return sum + (itemTotal * cgstRate) / 100;
      }, 0)
    : 0;
  
  const sgstAmount = formData.apply_taxes
    ? cartItems.reduce((sum, item) => {
        const itemTotal = getEffectivePrice(item) * item.quantity;
        const sgstRate = item.sgst_rate ?? 0;
        return sum + (itemTotal * sgstRate) / 100;
      }, 0)
    : 0;
  
  const taxAmount = cgstAmount + sgstAmount;
  const total = subtotal + taxAmount - discountAmount;

  const formatPrice = (price: number) => {
    return `₹${price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Checkout" size="lg">
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Icon name="CircleAlert" size={20} className="text-error-600" />
              <p className="text-error-700">{error}</p>
            </div>
          </div>
        )}

        {/* Warehouse Selection */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Warehouse <span className="text-error-600">*</span>
          </label>
          <select
            value={formData.warehouse_id}
            onChange={(e) => setFormData({ ...formData, warehouse_id: e.target.value })}
            disabled={isSubmitting}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100"
          >
            <option value="">Select warehouse</option>
            {warehouses?.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sale Date */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Sale Date</label>
          <Input
            type="datetime-local"
            value={
              formData.sale_date
                ? new Date(formData.sale_date).toISOString().slice(0, 16)
                : new Date().toISOString().slice(0, 16)
            }
            onChange={(e) =>
              setFormData({
                ...formData,
                sale_date: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              })
            }
            disabled={isSubmitting}
            className="w-full"
          />
        </div>

        {/* Sale Type */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Sale Type <span className="text-error-600">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() =>
                setFormData({ ...formData, sale_type: 'in_store', delivery_state: undefined })
              }
              disabled={isSubmitting}
              className={`p-4 rounded-lg border-2 transition-all ${
                formData.sale_type === 'in_store'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <Icon
                name="Store"
                size={24}
                className={`mx-auto mb-2 ${
                  formData.sale_type === 'in_store' ? 'text-primary-600' : 'text-neutral-400'
                }`}
              />
              <p
                className={`font-medium ${
                  formData.sale_type === 'in_store' ? 'text-primary-700' : 'text-neutral-700'
                }`}
              >
                In-Store
              </p>
            </button>
            <button
              onClick={() => setFormData({ ...formData, sale_type: 'delivery' })}
              disabled={isSubmitting}
              className={`p-4 rounded-lg border-2 transition-all ${
                formData.sale_type === 'delivery'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <Icon
                name="Truck"
                size={24}
                className={`mx-auto mb-2 ${
                  formData.sale_type === 'delivery' ? 'text-primary-600' : 'text-neutral-400'
                }`}
              />
              <p
                className={`font-medium ${
                  formData.sale_type === 'delivery' ? 'text-primary-700' : 'text-neutral-700'
                }`}
              >
                Delivery
              </p>
            </button>
          </div>
        </div>

        {/* Delivery State (only for delivery type) */}
        {formData.sale_type === 'delivery' && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Delivery State <span className="text-error-600">*</span>
            </label>
            <Input
              placeholder="Enter state code (e.g., TG, MH, KA)"
              value={formData.delivery_state || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  delivery_state: e.target.value.toUpperCase() || undefined,
                })
              }
              disabled={isSubmitting}
              maxLength={2}
              className="w-full uppercase"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Required for inter-state IGST calculation
            </p>
          </div>
        )}

        {/* Customer Details - searchable by mobile number */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Customer (Mobile Number){' '}
              {formData.sale_type === 'delivery' && <span className="text-error-600">*</span>}
            </label>

            {!customerInfo ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="Enter 10-digit mobile number"
                      value={customerMobile}
                      onChange={(e) => {
                        // Only allow numbers and limit to 10 digits
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setCustomerMobile(value);
                        setCustomerSearchError(null);
                        // Update formData.customer_phone immediately when user types
                        // This ensures the phone number is included even if user doesn't click search
                        setFormData((prev) => ({
                          ...prev,
                          customer_phone: value.length > 0 ? value : undefined,
                        }));
                      }}
                      disabled={isSubmitting || isSearching}
                      className={customerSearchError ? 'border-error-300' : ''}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleCustomerSearch}
                    disabled={isSubmitting || isSearching || customerMobile.length < 10}
                  >
                    {isSearching ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <Icon name="Search" size={16} />
                    )}
                  </Button>
                </div>
                {customerSearchError && (
                  <p className="text-xs text-error-600">{customerSearchError}</p>
                )}
                <p className="text-xs text-neutral-500">
                  Search for a registered customer or use mobile number as reference
                </p>
              </div>
            ) : (
              <div
                className={`p-3 rounded-lg border ${
                  customerInfo.isVerified
                    ? 'bg-success-50 border-success-200'
                    : 'bg-warning-50 border-warning-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div
                      className={`p-2 rounded-full ${
                        customerInfo.isVerified ? 'bg-success-100' : 'bg-warning-100'
                      }`}
                    >
                      <Icon
                        name={customerInfo.isVerified ? 'UserCheck' : 'User'}
                        size={16}
                        className={
                          customerInfo.isVerified ? 'text-success-600' : 'text-warning-600'
                        }
                      />
                    </div>
                    <div>
                      {customerInfo.name && (
                        <p className="font-medium text-neutral-900">{customerInfo.name}</p>
                      )}
                      <p className="text-sm text-neutral-600">{customerInfo.phone}</p>
                      <p className="text-xs mt-1">
                        {customerInfo.isVerified ? (
                          <span className="text-success-700">Verified Customer</span>
                        ) : (
                          <span className="text-warning-700">Guest Customer</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearCustomer}
                    disabled={isSubmitting}
                    title="Change customer"
                  >
                    <Icon name="X" size={16} className="text-neutral-600" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          {/* Customer Name Input */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Customer Name</label>
            <Input
              placeholder="Enter customer name (optional)"
              value={formData.customer_name || ''}
              onChange={(e) =>
                setFormData({ ...formData, customer_name: e.target.value || undefined })
              }
              disabled={isSubmitting}
            />
          </div>
          {/* Organization Member Checkbox */}
          {(() => {
            // Check if any cart item has member pricing available
            const hasMemberPricing = cartItems.some(
              (item) => item.memberPrice !== undefined && item.memberPrice > 0
            );
            const potentialSavings = hasMemberPricing
              ? cartItems.reduce((sum, item) => {
                  const retail = item.retailPrice ?? item.price ?? 0;
                  const member = item.memberPrice ?? retail;
                  return sum + (retail - member) * item.quantity;
                }, 0)
              : 0;

            return (
              <div
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  hasMemberPricing ? 'bg-success-50 border border-success-200' : 'bg-neutral-50'
                }`}
              >
                <input
                  type="checkbox"
                  id="is_org_member"
                  checked={formData.is_org_member || false}
                  onChange={(e) => setFormData({ ...formData, is_org_member: e.target.checked })}
                  disabled={isSubmitting}
                  className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <label htmlFor="is_org_member" className="text-sm font-medium text-neutral-700">
                    Organization Member
                  </label>
                  {hasMemberPricing && potentialSavings > 0 && (
                    <p className="text-xs text-success-700 mt-0.5">
                      Member pricing available - Save up to ₹
                      {potentialSavings.toLocaleString('en-IN')}
                    </p>
                  )}
                  {!hasMemberPricing && (
                    <p className="text-xs text-neutral-500 mt-0.5">
                      No member pricing set for these items
                    </p>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Payment Mode */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Payment Mode <span className="text-error-600">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {(['cash', 'upi', 'online'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setSelectedPaymentMode(mode)}
                disabled={isSubmitting}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedPaymentMode === mode
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <Icon
                  name={mode === 'cash' ? 'Banknote' : mode === 'upi' ? 'Smartphone' : 'CreditCard'}
                  size={20}
                  className={`mx-auto mb-1 ${
                    selectedPaymentMode === mode ? 'text-primary-600' : 'text-neutral-400'
                  }`}
                />
                <p
                  className={`text-sm font-medium capitalize ${
                    selectedPaymentMode === mode ? 'text-primary-700' : 'text-neutral-700'
                  }`}
                >
                  {mode}
                </p>
              </button>
            ))}
          </div>
          {selectedPaymentMode !== formData.payment_mode && (
            <div className="mt-3 flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setFormData({ ...formData, payment_mode: selectedPaymentMode })}
                disabled={isSubmitting}
                className="flex items-center gap-2"
              >
                <Icon name="Check" size={16} />
                Update Payment Mode
              </Button>
              <span className="text-xs text-neutral-500">
                Current: <span className="font-medium capitalize">{formData.payment_mode}</span>
              </span>
            </div>
          )}
        </div>

        {/* Auto Apply Discounts */}
        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
          <input
            type="checkbox"
            id="auto_apply_discounts"
            checked={formData.auto_apply_discounts ?? true}
            onChange={(e) => setFormData({ ...formData, auto_apply_discounts: e.target.checked })}
            disabled={isSubmitting}
            className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="auto_apply_discounts" className="text-sm font-medium text-neutral-700">
            Auto Apply Discounts
          </label>
        </div>

        {/* Discount Code Section */}
        <div className="space-y-3 p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
          <h4 className="font-semibold text-purple-900 flex items-center gap-2">
            <Icon name="Percent" size={16} />
            Apply Discount
          </h4>

          {!appliedDiscount ? (
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase());
                    setDiscountError(null);
                  }}
                  disabled={isSubmitting || isApplyingDiscount}
                  className={discountError ? 'border-error-300' : ''}
                />
                {discountError && <p className="mt-1 text-xs text-error-600">{discountError}</p>}
              </div>
              <Button
                variant="primary"
                onClick={handleApplyDiscount}
                disabled={isSubmitting || isApplyingDiscount || !discountCode.trim()}
                className="flex items-center gap-2"
              >
                {isApplyingDiscount ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Applying...
                  </>
                ) : (
                  <>
                    <Icon name="Check" size={16} />
                    Apply
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="bg-success-50 border border-success-200 rounded-lg p-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon name="CircleCheck" size={16} className="text-success-600" />
                    <span className="font-medium text-success-900">
                      {appliedDiscount.discount_name}
                    </span>
                  </div>
                  <p className="text-sm text-success-700 mt-1">
                    Code:{' '}
                    <span className="font-mono font-semibold">{appliedDiscount.discount_code}</span>
                  </p>
                  <p className="text-sm text-success-700">
                    Savings: <span className="font-semibold">{formatPrice(discountAmount)}</span>
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveDiscount}
                  disabled={isSubmitting}
                  title="Remove discount"
                >
                  <Icon name="X" size={16} className="text-error-600" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4">
          <h4 className="font-semibold text-primary-900 mb-3 flex items-center gap-2">
            <Icon name="FileText" size={16} />
            Order Summary
            {formData.is_org_member && (
              <span className="text-xs bg-success-100 text-success-700 px-2 py-0.5 rounded-full">
                Member Pricing Applied
              </span>
            )}
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-primary-700">Items</span>
              <span className="font-medium text-primary-900">{cartItems.length}</span>
            </div>
            {formData.is_org_member && memberSavings > 0 && (
              <div className="flex justify-between text-neutral-500">
                <span>Retail Price</span>
                <span className="line-through">{formatPrice(retailSubtotal)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-primary-700">
                {formData.is_org_member ? 'Member Subtotal' : 'Subtotal'}
              </span>
              <span className="font-medium text-primary-900">{formatPrice(subtotal)}</span>
            </div>
            {formData.is_org_member && memberSavings > 0 && (
              <div className="flex justify-between text-success-700">
                <span className="flex items-center gap-1">
                  <Icon name="UserCheck" size={14} />
                  Member Savings
                </span>
                <span className="font-medium">-{formatPrice(memberSavings)}</span>
              </div>
            )}
            {formData.apply_taxes && taxAmount > 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-primary-700">CGST</span>
                  <span className="font-medium text-primary-900">{formatPrice(cgstAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-700">SGST</span>
                  <span className="font-medium text-primary-900">{formatPrice(sgstAmount)}</span>
                </div>
              </>
            )}
            {appliedDiscount && discountAmount > 0 && (
              <div className="flex justify-between text-success-700">
                <span className="flex items-center gap-1">
                  <Icon name="Percent" size={14} />
                  Discount
                </span>
                <span className="font-medium">-{formatPrice(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold border-t border-primary-200 pt-2">
              <span className="text-primary-900">Total</span>
              <span className="text-primary-600">{formatPrice(total)}</span>
            </div>
            {(memberSavings > 0 || discountAmount > 0) && (
              <div className="text-xs text-success-700 text-right">
                Total Savings: {formatPrice(memberSavings + discountAmount)}!
              </div>
            )}
          </div>
        </div>

        {/* FEFO Batch Preview */}
        <div className="bg-info-50 border border-info-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-info-600 mt-0.5" />
            <div>
              <p className="text-sm text-info-700">
                <strong>FEFO Applied:</strong> Items will be deducted from batches with earliest
                expiry dates first
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-neutral-200">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processing...
              </>
            ) : (
              <>
                <Icon name="Check" size={20} className="mr-2" />
                Confirm Sale
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
